/**
 * TikTok Events API Service (Server-Side Tracking)
 * 
 * NOTE: This service is designed to communicate with the TikTok Events API.
 * In a production environment, this should ideally be called from a secure server (backend)
 * to avoid exposing the Access Token and to prevent CORS issues.
 * 
 * If used client-side, ensure your environment config handles security appropriately.
 */

const TIKTOK_ACCESS_TOKEN = process.env.REACT_APP_TIKTOK_ACCESS_TOKEN;
const TIKTOK_PIXEL_ID = process.env.REACT_APP_TIKTOK_PIXEL_ID || 'D5BT6KRC77UD75S4OG3G';
const API_URL = 'https://business-api.tiktok.com/open_api/v1.3/event/track/';

/**
 * Hash a string using SHA256 (Browser Compatible)
 * @param {string} message 
 * @returns {Promise<string>}
 */
const sha256 = async (message) => {
    if (!message) return null;
    const msgBuffer = new TextEncoder().encode(message.trim().toLowerCase());
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

/**
 * format payload for TikTok Events API
 */
const formatPayload = async (eventName, eventData, eventId, contextData) => {
    const emailHash = contextData.email ? await sha256(contextData.email) : undefined;
    const phoneHash = contextData.phone ? await sha256(contextData.phone) : undefined;

    return {
        pixel_code: TIKTOK_PIXEL_ID,
        event: eventName,
        event_id: eventId,
        timestamp: new Date().toISOString(),
        context: {
            user: {
                email: emailHash,
                phone_number: phoneHash,
            },
            page: {
                url: window.location.href,
            },
            user_agent: navigator.userAgent
        },
        properties: eventData
    };
};

/**
 * Send event to TikTok Events API
 * @param {string} eventName 
 * @param {Object} eventData 
 * @param {string} eventId 
 * @param {Object} contextData (email, phone, etc)
 */
export const trackServerEvent = async (eventName, eventData = {}, eventId, contextData = {}) => {
    if (!TIKTOK_ACCESS_TOKEN) {
        console.warn('[TikTok API] Access Token missing. Skipping server event.');
        return;
    }

    try {
        const payload = await formatPayload(eventName, eventData, eventId, contextData);

        // IMPORTANT: Calling this from client-side will likely trigger CORS errors
        // unless there is a proxy. If it fails, we catch it silently.
        const response = await fetch(API_URL, {
            method: 'POST',
            headers: {
                'Access-Token': TIKTOK_ACCESS_TOKEN,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
            // mode: 'no-cors' // Uncomment if CORS blocks you, but strict tracking functionality may be limited
        });

        if (!response.ok) {
            const error = await response.json();
            console.error('[TikTok API] Error:', error);
        } else {
            console.log('[TikTok API] Event sent successfully:', eventName);
        }
    } catch (error) {
        console.warn('[TikTok API] Request failed (likely CORS or Network):', error);
    }
};

export default {
    trackServerEvent
};
